import { useBoolean } from '@chakra-ui/hooks'
import { useToast } from '@chakra-ui/react'
import dayjs from 'dayjs'
import { createContext, FC, useCallback, useMemo, useState } from 'react'
import { AiOutlineBarChart } from 'react-icons/ai'
import { useImmer } from 'use-immer'

import { parseParametrizatioCurveWeightData } from '@helper/grid/parametrization-curve'
import {
  decrypterHeaderStorange,
  encrypteHeaderStorange
} from '@helper/util/cryptoStorange'
import parametrizationCurves from '@service/parametrization-curves'

import { ILayoutFilter } from '@base/@types/components/Layout/Filters'
import {
  ITableGridAction,
  ITableHeader
} from '@base/@types/components/Layout/TableGrid'
import { ILayoutToolsAction } from '@base/@types/components/Layout/Tools'
import { ICurveWeightProvider } from '@base/@types/providers/Parametrization/CurveWeightProvider'
import type {
  IParams,
  IParamsEdit,
  Pagination,
  ITooglesParams,
  IUpdateParams,
  IUpdateParamsEdit,
  IUpdateParamsDate
} from '@base/@types/providers/Parametrization/CurveWeightProvider'
import parametrizationCurveWeightHeader from '@base/variables/headers/parametrization/curve-weight'

const ParametrizationCurveWeightProvider: FC = ({ children }) => {
  const toast = useToast()
  const [loading, setLoading] = useState(false)
  const [filters, setFilters] = useBoolean(false)
  const [fastQuery, _setFastQuery] = useState<string>('')
  const [query, setQuery] = useState('')
  const [filename, setFilename] = useState('PRM Curva Peso - Relatório')
  const [_selected, _setSelected] = useState<any>()
  const [fetching, _setFetching] = useState(false)
  const [_error] = useState<string | undefined>()
  const [loadingEdit] = useState(false)
  const [pagination, updatePagination] = useImmer<Pagination>({
    actualPage: 0,
    rows: 0,
    totalPages: 0,
    totalRows: 0,
    skip: 0
  })
  const [data, updateData] = useImmer<any[]>([])
  const [selected, setSelectedOne] = useState(0)

  const [hiddenColumns, _updateHiddenColumns] = useImmer<string[]>([])
  const [filterEdit] = useImmer({
    centers: [],
    products: []
  })

  const [tooglesTable, updateToogleParams] = useImmer({
    pendentes: true,
    aprovados: true,
    recusados: true
  })

  const handleToogleTable = ({ name, value }: ITooglesParams) => {
    updateToogleParams(draft => {
      draft[name] = value
    })
  }

  const dateNow = new Date()
  const dateEnd = dateNow.setDate(dateNow.getDate() - 30)

  const [paramsDates, updateDatesParams] = useImmer({
    dateInit: dayjs(dateEnd).format('YYYY-MM-DD'),
    dateEnd: dayjs(new Date()).format('YYYY-MM-DD')
  })

  const handleParamsDate = ({ name, value }: IUpdateParamsDate) => {
    updateDatesParams(draft => {
      draft[name] = value
    })
  }

  const [paramsEdit, updateEditParams] = useImmer<IParamsEdit>({
    id: 0,
    curva: '',
    pesoLoja: 0,
    pesoEmpresa: 0,
    pesoVolume: 0,
    pesoCurva: 0,
    corteInicial: 0,
    corteFinal: 0
  })

  const handleUpdateEditParams = useCallback(
    async ({ name, value }: IUpdateParamsEdit) => {
      updateEditParams(draft => {
        draft[name] = value
      })
    },
    [updateEditParams]
  )

  const getCenters = useCallback(async () => {}, [])

  const [params, updateParams] = useImmer<IParams>({
    lojasIds: [],
    mundoIds: [],
    categoriasCompactasIds: [],
    fornecedoresIds: [],
    produtosIds: [],
    statusIds: [],
    curvaAbc: [],
    curvaIgualZero: false,
    produtoSemFornecedor: false
  })

  const [headers, updateHeaders] = useImmer<ITableHeader[]>(
    parametrizationCurveWeightHeader
  )
  const handleHeaderLocalStorange = useCallback(() => {
    if (!localStorage.getItem('parametrizationCurveWeightHeader')) {
      encrypteHeaderStorange('parametrizationCurveWeightHeader', headers)
    } else {
      localStorage.removeItem('parametrizationCurveWeightHeader')
      encrypteHeaderStorange('parametrizationCurveWeightHeader', headers)
    }
  }, [headers])

  const exportData = useCallback(async () => {}, [])

  const toggleColumn = useCallback(
    (index: number, column: number) => {
      const newHeaders = [...headers]

      newHeaders[index].columns[column] = {
        id: newHeaders[index].columns[column].id,
        name: newHeaders[index].columns[column].name,
        show: !newHeaders[index].columns[column].show,
        notShowColumn: newHeaders[index].columns[column].notShowColumn
          ? !newHeaders[index].columns[column].notShowColumn
          : true
      }

      updateHeaders(newHeaders)
      handleHeaderLocalStorange()
    },
    [handleHeaderLocalStorange, headers, updateHeaders]
  )
  const handleStorange = useCallback(async () => {
    if (localStorage.getItem('parametrizationCurveWeightHeader')) {
      const newHeader = decrypterHeaderStorange(
        'parametrizationCurveWeightHeader'
      )
      newHeader[0].columns[0] = {
        id: newHeader[0].columns[0].id,
        name: newHeader[0].columns[0].name,
        show: newHeader[0].columns[0].show
      }
      updateHeaders(newHeader)
    }
  }, [updateHeaders])
  const handleUpdateParams = ({ name, value }: IUpdateParams) => {
    updateParams(draft => {
      if (name === 'curvaIgualZero') {
        draft[name] = Boolean(value)
      } else {
        draft[name] = value
      }
      if (name === 'produtoSemFornecedor') {
        draft[name] = Boolean(value)
      } else {
        draft[name] = value
      }
    })
  }

  const handlePageChange = useCallback(async (_page: number) => {
    setLoading(true)
  }, [])

  const getData = useCallback(async () => {
    setLoading(true)
    // await getMaintence()

    encrypteHeaderStorange('parametrizationCurveWeightHeader', headers)

    handleStorange()

    try {
      parametrizationCurves
        .getParamsCurveWeight()
        .then(({ data: { data, pagination } }) => {
          updateData(draft => {
            return draft.filter(item => item.status === 99).concat(data)
          })

          updatePagination(pagination)
          setLoading(false)
        })
        .catch(() => {
          updateData([])
          setLoading(false)
        })
    } catch {
      setLoading(false)
    }
  }, [headers, handleStorange, updateData, updatePagination])

  const updateDataCurve = useCallback(async () => {
    setLoading(true)

    try {
      const { data: _res } = await parametrizationCurves.postParamsCurveWeight({
        id: paramsEdit.id,
        curva: paramsEdit.curva,
        pesoCurvaLoja: Number(paramsEdit.pesoLoja),
        pesoCurvaEmpresa: Number(paramsEdit.pesoEmpresa),
        pesoCurvaVolume: Number(paramsEdit.pesoVolume),
        pesoCurva: Number(paramsEdit.pesoCurva),
        corteInicial: Number(paramsEdit.corteInicial),
        corteFinal: Number(paramsEdit.corteFinal)
      })
      toast({
        title: 'Sucesso.',
        description: 'Curva Peso editado com Sucesso',
        status: 'success',
        duration: 2000,
        isClosable: true
      })
    } catch (error: any) {
      const message =
        error.response?.status === 400
          ? error.response?.data?.message
          : 'Ocorreu um problema interno. Tente novamente mais tarde'
      toast({
        title: 'Atenção.',
        description: message,
        status: 'warning',
        duration: 3000,
        isClosable: true
      })
      setLoading(false)
    }
    setSelectedOne(0)
    getData()
  }, [paramsEdit, toast, getData, setLoading, setSelectedOne])

  const deleteData = useCallback(async () => {
    setLoading(true)

    try {
      await parametrizationCurves.removeParamsCurveWeight({
        ids: [Number(selected)]
      })

      toast({
        title: 'Sucesso.',
        description: 'Registro deletado com Sucesso',
        status: 'success',
        duration: 2000,
        isClosable: true
      })
    } catch (error: any) {
      const message =
        error.response?.status === 400
          ? error.response?.data?.message
          : 'Ocorreu um problema interno. Tente novamente mais tarde'
      toast({
        title: 'Atenção.',
        description: message,
        status: 'warning',
        duration: 3000,
        isClosable: true
      })
      setLoading(false)
    }
    setSelectedOne(0)
    getData()
  }, [selected, getData, toast, setLoading, setSelectedOne])

  const createData = useCallback(async () => {
    setLoading(true)

    const validation = data.findIndex(
      item =>
        item.curva.toString().toLowerCase() ===
        String(paramsEdit.curva).toLowerCase()
    )
    if (validation !== -1) {
      toast({
        title: 'Atencao.',
        description: 'Essa curva já existe',
        status: 'warning',
        duration: 2000,
        isClosable: true
      })
      setLoading(false)

      return
    }
    try {
      await parametrizationCurves.postParamsCurveWeight({
        id: paramsEdit.id,
        curva: paramsEdit.curva,
        pesoCurvaLoja: Number(paramsEdit.pesoLoja),
        pesoCurvaEmpresa: Number(paramsEdit.pesoEmpresa),
        pesoCurvaVolume: Number(paramsEdit.pesoVolume),
        pesoCurva: Number(paramsEdit.pesoCurva),
        corteInicial: Number(paramsEdit.corteInicial),
        corteFinal: Number(paramsEdit.corteFinal)
      })
      toast({
        title: 'Sucesso.',
        description: 'Curva Peso cadastrado com Sucesso',
        status: 'success',
        duration: 2000,
        isClosable: true
      })
    } catch (error: any) {
      const message =
        error.response?.status === 400
          ? error.response?.data?.message
          : 'Ocorreu um problema interno. Tente novamente mais tarde'
      toast({
        title: 'Atenção.',
        description: message,
        status: 'warning',
        duration: 3000,
        isClosable: true
      })
      setLoading(false)
    }
    setSelectedOne(0)
    getData()
  }, [paramsEdit, data, getData, toast, setSelectedOne, setLoading])

  const filtersList = useMemo<ILayoutFilter[]>(() => {
    return []
  }, [])

  const actions = useMemo<ILayoutToolsAction[]>(() => {
    return [
      {
        type: 'export',
        exports: [
          {
            type: 'excel',
            entity: 'parameterCurveWeight',
            data,
            headers,
            parser: parseParametrizatioCurveWeightData
          }
        ]
      },
      {
        type: 'columns',
        data: headers,
        onToggleColumn: toggleColumn
      }
    ]
  }, [toggleColumn, data, headers])

  const handleEditParams = useCallback(
    async (index: any) => {
      const dataResponse = await data
      const idData = await dataResponse.filter(x => x.id === Number(index))
      if (idData.length > 0) {
        handleUpdateEditParams({
          name: 'id',
          value: idData[0].id
        })
        handleUpdateEditParams({
          name: 'curva',
          value: idData[0].curva
        })
        handleUpdateEditParams({
          name: 'pesoLoja',
          value: idData[0].pesoCurvaLoja
        })
        handleUpdateEditParams({
          name: 'pesoEmpresa',
          value: idData[0].pesoCurvaEmpresa
        })
        handleUpdateEditParams({
          name: 'pesoVolume',
          value: idData[0].pesoCurvaVolume
        })
        handleUpdateEditParams({
          name: 'pesoCurva',
          value: idData[0].pesoCurva
        })
        handleUpdateEditParams({
          name: 'corteInicial',
          value: idData[0].corteInicial
        })
        handleUpdateEditParams({
          name: 'corteFinal',
          value: idData[0].corteFinal
        })
      }
    },
    [handleUpdateEditParams, data]
  )

  const handleSelect = useCallback(
    async (index: any) => {
      const similarProduct = index['id-id']
      const newSelected = selected

      setSelectedOne(prev => (prev !== similarProduct ? similarProduct : 0))
      if (newSelected === similarProduct) {
        updateEditParams({
          id: 0,
          curva: 0,
          pesoLoja: 0,
          pesoEmpresa: 0,
          pesoVolume: 0,
          pesoCurva: 0,
          corteInicial: 0,
          corteFinal: 0
        })
      } else {
        handleEditParams(index['id-id'])
      }
    },
    [setSelectedOne, handleEditParams, selected, updateEditParams]
  )

  const tableInteraction = useMemo<ITableGridAction[]>(() => {
    return [
      {
        type: 'checkbox',
        icon: <AiOutlineBarChart />,
        onChange: handleSelect,
        selecteds: selected,
        isMulti: false
      }
    ]
  }, [selected, handleSelect])

  return (
    <ParametrizationCurveWeightContext.Provider
      value={{
        loading,
        loadingEdit,
        data: parseParametrizatioCurveWeightData(data),
        params,
        headers,
        fetching,
        actions,
        tableInteraction,
        selected,
        filterEdit,
        paramsEdit,
        paramsDates,
        tooglesTable,
        filename,
        query,
        filters,
        hiddenColumns,
        handlePageChange,
        pagination,
        fastQuery,
        filtersList,
        toggleColumn,
        handleUpdateParams,
        handleSelect,
        getCenters,
        handleParamsDate,
        setQuery,
        getData,
        exportData,
        createData,
        handleUpdateEditParams,
        handleToogleTable,
        updateDataCurve,
        deleteData,
        setFilters,
        setFilename
      }}
    >
      {children}
    </ParametrizationCurveWeightContext.Provider>
  )
}

export default ParametrizationCurveWeightProvider
export const ParametrizationCurveWeightContext =
  createContext<ICurveWeightProvider>({} as ICurveWeightProvider)
